import {
  AorCancellationStatus,
  BenefitStatus,
  BusinessEntityType,
  CarrierPaymentMethod,
  CarrierRequestStatus,
  CitizenshipStatus,
  CoverageChange,
  EmployeePaymentMethodType,
  EmployeeRole,
  EmployerStatus,
  EmploymentType,
  InsuredSubtype,
  MaritalStatus,
  OEPPaymentMethod,
  OnboardingType,
  PolicyType,
  QualifyingLifeEventType,
  Role,
  SelfEnrollType,
  SelfReportType,
  SubmissionType,
  WageType,
} from '@zorro/clients';
import { $enum } from 'ts-enum-util';

import { DeepReadonly } from './types';

export const recordsPerPage = 100;

export const EMPLOYEE_BREAKDOWN_PAGE_WIDTH = 1085;
export const SHOPPING_EXPERIENCE_PAGE_WIDTH = 620;

export const EMPLOYEE_SIDEBAR_ZINDEX = 50;
export const MODAL_ZINDEX = 300;
export const DRAWER_CONTENT_ZINDEX = 210;
export const DRAWER_OVERLAY_ZINDEX = 200;
/** Floating SelectionBar — above page chrome/sidebar, below drawers and modals. */
export const SELECTION_BAR_ZINDEX = 180;
/** Full-viewport layers — cover the header and selection bar, stay below drawers and modals. */
export const FULL_VIEW_ZINDEX = 190;
/**
 * Dropdown menu panels — must sit above modals/drawers so a menu opened from
 * inside one is not clipped. Mantine's own default (300) ties `MODAL_ZINDEX`,
 * so this keeps the 500 the retired V1 `RadixMenu` used.
 */
export const MENU_ZINDEX = 500;
export const DEPENDENT_INELIGIBLE_AGE = 26;

export const STEPPER_FLOW_STYLES = { steps: { maxWidth: '880px' } };
export const STEPPER_BODY_MAX_WIDTH = 928;

export const zorroBundleExampleRecommendations = [
  "A $0 deductible/EPO plan, because it will cover medical costs from the get-go, and so that you'd be covered by a wide network of providers",
  'Dental insurance, because your dependents are likely to need it',
  'Life and Critical Illness insurances to protect your loved ones in the event something happens to you or your spouse',
];

export const planTooltipLabels = {
  deductible: `The amount you pay before your insurance
               covers certain services. Note: Some basic
               services might be covered before reaching
               the deductible.`,
  maxOutOfPocket: `The most you'll pay in a year for covered health
                   services. After this, insurance covers 100%.
                   Some costs might not count towards this limit.`,
  employer:
    'The amount your employer pays towards your health insurance every month.',
  hsa: `HSA stands for Health Savings Account, letting
        you save money tax-free for medical expenses.
        Some plans are HSA-eligible, while others are not.`,
  planMarket: `Indicates if a plan is available off-exchange (pre-tax), on-exchange (post-tax), or both.
               For both-market plans, we make a best-effort attempt to enroll you off-exchange.`,
  metalLevel: `Metal levels are used to categorize plan costs.
               'Bronze' has the lowest premium & highest
               out-of-pocket, 'Silver' is medium, and 'Gold' &
               'Platinum' have higher premiums & lower
               out-of-pocket costs.`,
  allInCost: `An estimate of your potential annual cost rate
              for this plan, benchmarked against the variety
              of plans available. All-In-Cost factors in premiums,
              deductibles, out-of-pocket expenses, and your
              personal care needs.`,
  selfEnroll: `Self-enroll plans require you to complete and submit your application directly to the insurance carrier.
               Zorro cannot enroll you in these specific plans, but we will provide step-by-step instructions if you choose one`,
  selfEnrollAdmin: `Self-enroll plans must be submitted by the employee directly to the carrier.
               Zorro cannot enroll the employee in this plan, but provides step-by-step instructions in the employee's flow`,
  easyEnroll: `Easy enroll plans are handled entirely by Zorro — we submit your application,
               set up your payment, and manage your enrollment from start to finish.
               There's nothing further you need to do.`,
  easyEnrollAdmin: `Easy enroll plans are submitted and managed by Zorro on the employee's
               behalf, end to end. No action is required from the employee.`,
  extraSteps: `Extra steps plans are submitted by Zorro, but you'll need to complete one or more
               steps yourself before your coverage starts. The steps are listed in the plan's full details.`,
  extraStepsAdmin: `Extra steps plans are submitted by Zorro, but the employee must complete one or more
               follow-up steps themselves before coverage starts. The steps are listed in the plan's full details.`,
};

export enum OpenEnrollmentPeriodsRelevancyMode {
  EFFECTIVE = 'EFFECTIVE',
  ONBOARDABLE = 'ONBOARDABLE',
}

export const qualifyingLifeEventLabelConfig: DeepReadonly<{
  [key in QualifyingLifeEventType]: string;
}> = {
  [QualifyingLifeEventType.MARRIED]: 'Marriage',
  [QualifyingLifeEventType.DIVORCED]: 'Divorce',
  [QualifyingLifeEventType.NEW_DEPENDENT]: 'Birth or new dependent',
  [QualifyingLifeEventType.DEATH_IN_FAMILY]: 'Death in family',
  [QualifyingLifeEventType.RELOCATION]: 'Change in residence',
  [QualifyingLifeEventType.ELIGIBILITY]: 'Child turned 26',
  [QualifyingLifeEventType.OTHER]: 'Other reason',
};

export const selfReportTypeLabelConfig: DeepReadonly<{
  [key in SelfReportType]: string;
}> = {
  [SelfReportType.NEW_ENTRY]: 'New entry',
  [SelfReportType.CONFIRMED]: 'Confirmed',
  [SelfReportType.UPDATED]: 'Updated',
  [SelfReportType.NOT_APPLICABLE]: 'Not self-reported',
};

export const businessEntityTypeLabelConfig: DeepReadonly<{
  [key in BusinessEntityType]: string;
}> = {
  [BusinessEntityType.C_CORPORATION]: 'C corporation',
  [BusinessEntityType.S_CORPORATION]: 'S corporation',
  [BusinessEntityType.GOVERNMENT_AGENCY]: 'Government agency',
  [BusinessEntityType.LIMITED_LIABILITY_CORPORATION]:
    'Limited liability corporation',
  [BusinessEntityType.LIMITED_LIABILITY_PARTNERSHIP]:
    'Limited liability partnership',
  [BusinessEntityType.NON_PROFIT_ORGANIZATION]: 'Non profit organization',
  [BusinessEntityType.OTHER]: 'Other',
  [BusinessEntityType.PARTNERSHIP]: 'Partnership',
  [BusinessEntityType.SOLE_PROPRIETORSHIP]: 'Sole proprietorship',
  [BusinessEntityType.UNION]: 'Union',
};

export const oepPaymentMethodLabelConfig: DeepReadonly<{
  [key in OEPPaymentMethod]: string;
}> = {
  [OEPPaymentMethod.ZORRO_PAY]: 'Zorro Pay',
  [OEPPaymentMethod.SELF_PAY]: 'Self-pay (reimbursement)',
};

export const citizenshipStatusLabelConfig: DeepReadonly<{
  [key in CitizenshipStatus]: string;
}> = {
  [CitizenshipStatus.RESIDENT_ALIEN]: 'Resident alien',
  [CitizenshipStatus.US_CITIZEN]: 'US citizen',
  [CitizenshipStatus.OTHER]: 'Other',
};

export const maritalStatusLabelConfig: DeepReadonly<{
  [key in MaritalStatus]: string;
}> = {
  [MaritalStatus.SINGLE]: 'Single',
  [MaritalStatus.MARRIED]: 'Married',
  [MaritalStatus.IN_DOMESTIC_PARTNERSHIP]: 'In a domestic partnership',
};

export const employmentTypeLabelConfig: DeepReadonly<{
  [key in EmploymentType]: string;
}> = {
  [EmploymentType.FULL_TIME]: 'Full time',
  [EmploymentType.PART_TIME]: 'Part time',
};

export const wageTypeLabelConfig: DeepReadonly<{
  [key in WageType]: string;
}> = {
  [WageType.HOURLY]: 'Hourly',
  [WageType.SALARY]: 'Salary',
};

export const paymentMethodLabelConfig: DeepReadonly<{
  [key in EmployeePaymentMethodType]: string;
}> = {
  [EmployeePaymentMethodType.ZORRO_PAY]: 'Zorro Pay',
  [EmployeePaymentMethodType.COMBINED]: 'Combined',
  [EmployeePaymentMethodType.SELF_PAY]: 'Self Pay',
};

export const submissionTypeToStringMap: {
  [key in SubmissionType]: string;
} = {
  [SubmissionType.BY_EMPLOYEE]: 'By employee',
  [SubmissionType.BY_EMPLOYER_ADMIN]: 'By employer admin',
  [SubmissionType.BY_AGENT]: 'By enrollment agent',
  [SubmissionType.BY_OPERATOR]: 'By operator',
  [SubmissionType.SELF_REPORTED]: 'Self reported',
  [SubmissionType.AUTOMATIC]: 'By system',
  [SubmissionType.NA]: 'Not Applicable',
};

export const insuredSubtypeLabelConfig: DeepReadonly<{
  [key in InsuredSubtype]: string;
}> = {
  [InsuredSubtype.SPOUSE]: 'Spouse',
  [InsuredSubtype.DOMESTIC_PARTNER]: 'Domestic partner',
  [InsuredSubtype.CHILD]: 'Child',
  [InsuredSubtype.OTHER_DEPENDENT]: 'Other dependent',
};

export const getRoleLabel = (role: Role) =>
  $enum.mapValue(role).with({
    [Role.ANY]: 'Any',
    [Role.EMPLOYEE]: 'Employee',
    [Role.EMPLOYER_ADMIN]: 'Admin',
    [Role.OMNIPOTENT_ADMIN]: 'Omnipotent admin',
    [Role.AGENT]: 'Agent',
    [Role.AGENCY_ADMIN]: 'Agency admin',
    [Role.ACCOUNT_SUPERVISOR]: 'Account supervisor',
    [Role.OPERATOR]: 'Operator',
    [Role.USER]: 'User',
  });

export const employerStatusLabelConfig: DeepReadonly<{
  [key in EmployerStatus]: string;
}> = {
  [EmployerStatus.ACTIVE]: 'Active',
  [EmployerStatus.DEACTIVATED]: 'Deactivated',
  [EmployerStatus.DRAFT]: 'Draft',
  [EmployerStatus.IN_ZORRO_SETUP]: 'In zorro setup',
  [EmployerStatus.NEEDS_REVIEW]: 'Needs review',
  [EmployerStatus.PROSPECT]: 'Prospect',
  [EmployerStatus.WAITING_FOR_EMPLOYER]: 'Waiting for employer',
};

export const selfEnrollTypeLabelConfig: DeepReadonly<{
  [key in SelfEnrollType]: string;
}> = {
  [SelfEnrollType.SELF_ENROLL]: 'Yes',
  [SelfEnrollType.NOT_SELF_ENROLL]: 'No',
  [SelfEnrollType.UNKNOWN]: '-',
};

export const coverageChangeLabelConfig: DeepReadonly<{
  [key in CoverageChange]: string;
}> = {
  [CoverageChange.NO_PRIOR_COVERAGE]: 'No prior coverage',
  [CoverageChange.COVERAGE_GAP]: 'Coverage gap',
  [CoverageChange.WAIVED]: 'Waived',
  [CoverageChange.PENDING_ELECTION]: 'Pending election',
  [CoverageChange.MARKET_CHANGE_ON_TO_OFF]: 'Market change (on to off)',
  [CoverageChange.MARKET_CHANGE_OFF_TO_ON]: 'Market change (off to on)',
  [CoverageChange.CARRIER_CHANGE]: 'Carrier change',
  [CoverageChange.PLAN_CHANGE]: 'Plan change',
  [CoverageChange.PLAN_UPDATE]: 'Plan update',
  [CoverageChange.RENEWAL_AOR_GAINED]: 'Renewal (AOR gained)',
  [CoverageChange.RENEWAL]: 'Renewal',
  [CoverageChange.UNCLASSIFIED]: 'Unclassified',
};

export const PolicyTypeLabelConfig: DeepReadonly<{
  [key in PolicyType]: string;
}> = {
  [PolicyType.MEDICARE]: 'Medicare',
  [PolicyType.IFP]: 'IFP',
  [PolicyType.MEDICARE_IFP]: 'Medicare + IFP',
};

export const aorCancellationStatusLabelConfig: DeepReadonly<{
  [key in AorCancellationStatus]: string;
}> = {
  [AorCancellationStatus.CONFIRMED_BY_EMPLOYEE]: 'Confirmed by employee',
  [AorCancellationStatus.AWAITING_EMPLOYEE]: 'Awaiting employee',
  [AorCancellationStatus.NOT_APPLICABLE]: 'Not applicable',
};

export const CARRIER_CSV_TEMPLATES = [
  'Highmark BlueCross BlueShield 2026',
  'UPMC 2026',
  'HSA 2026',
] as const;

export type CarrierTemplate = (typeof CARRIER_CSV_TEMPLATES)[number];

export const carrierRequestStatusLabelConfig: DeepReadonly<{
  [key in CarrierRequestStatus]: string;
}> = {
  [CarrierRequestStatus.CONFIRMED_BY_EMPLOYEE]: 'Confirmed by employee',
  [CarrierRequestStatus.AWAITING_EMPLOYEE]: 'Awaiting employee',
  [CarrierRequestStatus.NOT_APPLICABLE]: 'Not applicable',
};

export const onboardingTypeLabelConfig: DeepReadonly<{
  [key in OnboardingType]: string;
}> = {
  [OnboardingType.OPEN_ENROLLMENT]: 'Open enrollment',
  [OnboardingType.SPECIAL]: 'Special',
};

export const employeeRoleLabelConfig: DeepReadonly<{
  [key in EmployeeRole]: string;
}> = {
  [EmployeeRole.EMPLOYEE]: 'Employee',
  [EmployeeRole.EMPLOYER_ADMIN]: 'Admin',
  [EmployeeRole.OTHER]: 'Other',
};

export const benefitStatusLabelConfig: DeepReadonly<{
  [key in BenefitStatus]: string;
}> = {
  [BenefitStatus.IN_CART]: 'In cart',
  [BenefitStatus.AWAITING_MEDICAL]: 'Awaiting medical',
  [BenefitStatus.READY_TO_APPLY]: 'Ready to apply',
  [BenefitStatus.AWAITING_EMPLOYEE]: 'Awaiting employee',
  [BenefitStatus.CARRIER_APPLICATION_SENT]: 'Carrier application sent',
  [BenefitStatus.AWAITING_PAYMENT]: 'Awaiting payment',
  [BenefitStatus.ENROLLMENT_CONFIRMED]: 'Enrolled',
  [BenefitStatus.ACTIVE]: 'Active coverage',
  [BenefitStatus.ENDED]: 'Coverage ended',
};

export const carrierPaymentMethodLabelConfig: DeepReadonly<{
  [key in CarrierPaymentMethod]: string;
}> = {
  [CarrierPaymentMethod.ACH]: 'ACH',
  [CarrierPaymentMethod.CARD]: 'Card',
};
